import { LockOutlined as LockOutlinedIcon } from 'components/icons';
import StatusChip from 'components/@extended/StatusChip.tsx';
import PermissionButton from 'components/buttons/PermissionButton';
import { FormattedDate } from 'components/misc/FormattedDate';
import { ConfidentialChip } from 'components/matters/MatterIndicators';
import PageHeader from 'components/PageHeader';
import { RecordLink } from 'components/record-chip/RecordLink';
import { META_LINK_CLASS, MetaItem, RecordHeader, RecordMoreMenu } from 'components/record-header/RecordHeader';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { FormattedMessage } from 'react-intl';
import { ExperienceRecordDetailsDto } from 'types/experience-records';
import { t } from 'utils/getTranslations.ts';
import { useHasPermissions } from 'utils/permissionUtil';
import { useGoBack, useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import { XrDraftChip } from './components/XrDraftChip';
import { useExperienceRecordDelete } from './useExperienceRecordDelete';

interface Props {
    experienceRecord: ExperienceRecordDetailsDto;
}

const ExperienceRecordPageHeader = ({ experienceRecord }: Props) => {
    const navigateWithReturn = useNavigateWithReturnState();
    const goBack = useGoBack('/experience-records');
    const isNew = useIsNewDesign();
    const canDelete = useHasPermissions(['DELETE.EXPERIENCE_RECORDS.ALL']);
    const handleDelete = useExperienceRecordDelete(String(experienceRecord?.id));

    if (!experienceRecord) {
        return null;
    }

    const draftChip = experienceRecord.status === 'DRAFT' && <XrDraftChip className="self-start" />;
    const confidentialChip = experienceRecord.isConfidential && (
        <StatusChip
            className="self-start text-xs"
            icon={<LockOutlinedIcon className="text-fg-warning h-4 w-4" />}
            label={t('experience-records.view.header.confidential')}
            type="warning"
        />
    );

    if (isNew) {
        // The cards edit in place, so no Edit; Delete sits under ⋯.
        return (
            <RecordHeader
                chips={
                    <>
                        {draftChip}
                        {experienceRecord.isConfidential && (
                            <ConfidentialChip label={t('experience-records.view.header.confidential')} />
                        )}
                    </>
                }
                title={experienceRecord.name || ''}
                pin={{ kind: 'experience-records', id: experienceRecord.id }}
                meta={
                    <>
                        {experienceRecord.clientParty?.id ? (
                            <MetaItem label={<FormattedMessage id="experience-records.view.client" />}>
                                <RecordLink
                                    className={META_LINK_CLASS}
                                    to={`/contact/${experienceRecord.clientParty.id}`}
                                    label={experienceRecord.clientParty.name}
                                />
                            </MetaItem>
                        ) : null}
                        {experienceRecord.date ? (
                            <MetaItem label={<FormattedMessage id="date" />}>
                                <FormattedDate value={experienceRecord.date} />
                            </MetaItem>
                        ) : null}
                    </>
                }
                actions={
                    <RecordMoreMenu
                        menuItems={
                            canDelete ? [{ title: t('delete'), variant: 'destructive', handler: handleDelete }] : []
                        }
                    />
                }
            />
        );
    }

    return (
        <PageHeader
            title={experienceRecord.name || ''}
            showBreadcrumb
            onBack={goBack}
            buttons={
                <div className="flex flex-row justify-end gap-2">
                    <PermissionButton
                        permissions={['UPDATE.EXPERIENCE_RECORDS.ALL']}
                        variant="outlined"
                        size="small"
                        onClick={() => navigateWithReturn(`/experience-records/${experienceRecord.id}/edit`)}
                    >
                        <FormattedMessage id="edit" />
                    </PermissionButton>
                </div>
            }
        >
            <div className="flex w-full flex-row flex-wrap items-center justify-start gap-2 py-4 sm:gap-4">
                {draftChip}
                {confidentialChip}
            </div>
        </PageHeader>
    );
};

export default ExperienceRecordPageHeader;
